import { SeoHead } from "@/components/SeoHead";

const FAQ_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "如何取消訂閱？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "登入後前往「我的帳戶」→「訂閱管理」，按「管理訂閱」即可取消。取消後服務將繼續至當前計費週期結束。"
      }
    },
    {
      "@type": "Question",
      "name": "付費後何時生效？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "付款成功後即時生效，您可立即查看學校名稱及聯絡資料。"
      }
    },
    {
      "@type": "Question",
      "name": "如何接收招標通知？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "付費會員可在「我的帳戶」→「通知設定」設定電郵通知及綁定 Telegram，按類別及地區篩選感興趣的招標。"
      }
    },
    {
      "@type": "Question",
      "name": "招標資訊每天何時更新？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "本平台每日自動更新各學校的最新招標資訊，付費會員將於下午收到當日通知。"
      }
    }
  ]
});

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="聯絡我們"
        description="如有任何關於 Skoltend 帳戶、訂閱或平台功能的疑問，請透過電郵 cs@skoltend.com 聯絡我們，我們一般於 1–2 個工作天內回覆。"
        canonical="/contact"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_JSON_LD }} />
      <div className="border-b">
        <div className="relative w-full overflow-hidden" style={{ maxHeight: "360px" }}>
          <img
            src="/contact-hero.jpg"
            alt="Skoltend 客戶服務團隊，專業人員隨時解答香港學校招標採購查詢"
            className="w-full object-cover object-center"
            style={{ maxHeight: "360px" }}
            width={1200}
            height={630}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
        </div>
        <div className="bg-secondary/30 py-8">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <p className="text-xs font-medium text-primary uppercase tracking-widest mb-2">聯絡我們</p>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-snug">聯絡我們</h1>
            <p className="text-muted-foreground text-sm">我們樂意解答您的任何疑問</p>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl space-y-8 text-sm leading-relaxed text-foreground">

        <section className="space-y-3">
          <h2 className="text-base font-semibold">客戶服務</h2>
          <p>如您對帳戶、訂閱或平台功能有任何疑問，請透過電郵聯絡我們：</p>
          <p>
            <a href="mailto:cs@skoltend.com" className="text-primary hover:underline font-medium text-base">
              cs@skoltend.com
            </a>
          </p>
          <p className="text-muted-foreground">我們一般於 1–2 個工作天內回覆。</p>
        </section>

        <div className="border rounded-lg divide-y">
          <div className="p-5 space-y-1">
            <p className="font-medium">帳戶及訂閱問題</p>
            <p className="text-muted-foreground">付款、取消訂閱、升級或帳戶設定</p>
            <a href="mailto:cs@skoltend.com?subject=帳戶查詢" className="text-primary hover:underline text-xs">
              cs@skoltend.com
            </a>
          </div>
          <div className="p-5 space-y-1">
            <p className="font-medium">招標資訊查詢</p>
            <p className="text-muted-foreground">資訊有誤、缺漏招標或建議新增學校</p>
            <a href="mailto:cs@skoltend.com?subject=招標資訊查詢" className="text-primary hover:underline text-xs">
              cs@skoltend.com
            </a>
          </div>
          <div className="p-5 space-y-1">
            <p className="font-medium">商務合作</p>
            <p className="text-muted-foreground">企業訂閱、廣告或其他商業合作</p>
            <a href="mailto:cs@skoltend.com?subject=商務合作" className="text-primary hover:underline text-xs">
              cs@skoltend.com
            </a>
          </div>
        </div>

        <section className="space-y-4">
          <h2 className="text-base font-semibold">關注我們</h2>
          <p className="text-muted-foreground">掃描 QR 碼或點擊連結，關注 Skoltend 各大社交平台，第一時間獲取最新招標資訊。</p>
          <div className="grid grid-cols-2 gap-4">

            {/* Facebook */}
            <a
              href="https://www.facebook.com/skoltend"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border bg-card p-4 hover:border-[#1877F2]/50 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                <span className="text-sm font-semibold text-[#1877F2]">Facebook</span>
              </div>
              <img
                src="/qrcode-facebook.png"
                alt="Skoltend Facebook QR Code"
                className="w-[140px] h-[140px] object-contain rounded-lg"
              />
              <span className="text-xs text-muted-foreground group-hover:text-[#1877F2] transition-colors">Skoltend</span>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/skoltend"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border bg-card p-4 hover:border-[#E1306C]/50 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="url(#ig-grad)">
                  <defs>
                    <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FFDC80"/>
                      <stop offset="30%" stopColor="#F56040"/>
                      <stop offset="60%" stopColor="#C13584"/>
                      <stop offset="100%" stopColor="#833AB4"/>
                    </linearGradient>
                  </defs>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                <span className="text-sm font-semibold bg-gradient-to-r from-[#F56040] to-[#C13584] bg-clip-text text-transparent">Instagram</span>
              </div>
              <img
                src="/qrcode-instagram.jpg"
                alt="Skoltend Instagram QR Code"
                className="w-[140px] h-[140px] object-contain rounded-lg"
              />
              <span className="text-xs text-muted-foreground group-hover:text-[#C13584] transition-colors">@skoltend</span>
            </a>

            {/* Threads */}
            <a
              href="https://www.threads.net/@skoltend"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border bg-card p-4 hover:border-foreground/30 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 192 192" fill="currentColor"><path d="M141.537 88.988a66.667 66.667 0 00-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.107 5.864 14.055-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C35.236 139.966 29.808 120.682 29.605 96c.203-24.682 5.63-43.966 16.133-57.317C56.954 24.425 74.204 17.11 97.013 16.94c22.975.17 40.526 7.52 52.171 21.847 5.71 7.026 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.07 0h-.113C68.882.194 47.292 9.642 32.788 28.08 19.882 44.485 13.224 67.315 13.001 95.932L13 96v.067c.224 28.617 6.882 51.447 19.788 67.854C47.292 182.358 68.882 191.806 96.957 192h.113c24.96-.173 42.554-6.708 57.048-21.189 18.963-18.945 18.392-42.692 12.142-57.27-4.484-10.454-13.033-18.945-24.723-24.553zm-43.897 40.601c-10.432.572-21.27-4.1-21.82-14.146-.412-7.73 5.52-16.355 23.493-17.396 2.057-.119 4.074-.178 6.052-.178 6.271 0 12.136.606 17.518 1.774-1.994 24.995-15.555 29.38-25.243 29.946z"/></svg>
                <span className="text-sm font-semibold">Threads</span>
              </div>
              <img
                src="/qrcode-threads.jpg"
                alt="Skoltend Threads QR Code"
                className="w-[140px] h-[140px] object-contain rounded-lg"
              />
              <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">@skoltend</span>
            </a>

            {/* Telegram */}
            <a
              href="https://t.me/Skoltend"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border bg-card p-4 hover:border-[#26A5E4]/50 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#26A5E4"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                <span className="text-sm font-semibold text-[#26A5E4]">Telegram</span>
              </div>
              <img
                src="/qrcode-telegram.jpg"
                alt="Skoltend Telegram QR Code"
                className="w-[140px] h-[140px] object-contain rounded-lg"
              />
              <span className="text-xs text-muted-foreground group-hover:text-[#26A5E4] transition-colors">@skoltend</span>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/85265498024"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border bg-card p-4 hover:border-[#25D366]/50 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12.04 0C5.446 0 .06 5.386.06 11.984c0 2.112.554 4.169 1.606 5.986L0 24l6.185-1.62a11.9 11.9 0 005.855 1.494h.005c6.593 0 11.98-5.386 11.98-11.985 0-3.2-1.245-6.21-3.505-8.47A11.916 11.916 0 0012.04 0zm0 21.985a9.94 9.94 0 01-5.073-1.393l-.364-.216-3.67.962.982-3.584-.238-.368a9.916 9.916 0 01-1.52-5.402c0-5.494 4.47-9.964 9.97-9.964a9.9 9.9 0 017.043 2.92 9.9 9.9 0 012.917 7.045c0 5.494-4.47 9.964-9.965 9.964z"/>
                </svg>
                <span className="text-sm font-semibold text-[#25D366]">WhatsApp</span>
              </div>
              <img
                src="/qrcode-whatsapp-contact-20260915.png"
                alt="Skoltend WhatsApp QR Code"
                className="w-[140px] h-[140px] object-contain rounded-lg"
              />
              <span className="text-xs text-muted-foreground group-hover:text-[#25D366] transition-colors">+852 6549 8024</span>
            </a>

          </div>
        </section>

        <section className="space-y-3 border-t pt-8">
          <h2 className="text-base font-semibold">常見問題</h2>

          <div className="space-y-4">
            <div className="space-y-1">
              <p className="font-medium">如何取消訂閱？</p>
              <p className="text-muted-foreground">
                登入後前往「我的帳戶」→「訂閱管理」，按「管理訂閱」即可取消。取消後服務將繼續至當前計費週期結束。
              </p>
            </div>
            <div className="space-y-1">
              <p className="font-medium">付費後何時生效？</p>
              <p className="text-muted-foreground">
                付款成功後即時生效，您可立即查看學校名稱及聯絡資料。
              </p>
            </div>
            <div className="space-y-1">
              <p className="font-medium">如何接收招標通知？</p>
              <p className="text-muted-foreground">
                付費會員可在「我的帳戶」→「通知設定」設定電郵通知及綁定 Telegram，按類別及地區篩選感興趣的招標。
              </p>
            </div>
            <div className="space-y-1">
              <p className="font-medium">招標資訊每天何時更新？</p>
              <p className="text-muted-foreground">本平台每日自動更新各學校的最新招標資訊，付費會員將於下午收到當日通知。</p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
